import { Component, ElementRef, EventEmitter, Input, Output, HostListener, ChangeDetectorRef, TemplateRef } from '@angular/core';

@Component({
  selector: 'app-combobox-dropdown',
  template: `
    <ul
      [id]="listboxId"
      role="listbox"
      [attr.aria-label]="ariaLabel"
      [style.max-height.px]="maxHeight"
      class="combobox-dropdown"
    >
      <li
        *ngFor="let option of options; let i = index"
        [id]="optionPrefix + '-option-' + i"
        role="option"
        [class.highlighted]="i === highlightedIndex && !keyboardNavigationMode"
        [class.keyboard-focused]="i === highlightedIndex && keyboardNavigationMode"
        [attr.aria-selected]="i === highlightedIndex && keyboardNavigationMode ? 'true' : null"
        (mousedown)="onMouseDown($event)"
        (click)="selectOption(option)"
        (mouseenter)="onMouseEnter(i)"
        class="combobox-option"
      >
        <ng-container *ngIf="optionTemplate; else defaultTemplate">
          <ng-container *ngTemplateOutlet="optionTemplate; context: { $implicit: option, index: i }"></ng-container>
        </ng-container>
        <ng-template #defaultTemplate>{{ getDisplayValue(option) }}</ng-template>
      </li>
      <li *ngIf="options.length === 0" class="combobox-option combobox-no-results" role="option" aria-disabled="true">
        {{ noResultsText }}
      </li>
    </ul>
  `,
  styles: [`
    :host {
      display: block;
    }

    .combobox-dropdown {
      list-style: none;
      margin: 0;
      padding: 0;
      background: white;
      border: 1px solid #ccc;
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      overflow-y: auto;
      overflow-x: hidden;
    }

    .combobox-option {
      padding: 8px 12px;
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: background-color 0.2s ease;
    }

    .combobox-option:hover,
    .combobox-option.highlighted {
      background-color: #f0f0f0;
    }

    .combobox-option.keyboard-focused {
      background-color: #cce5ff;
      outline: 2px solid #0078d4;
      outline-offset: -2px;
    }

    .combobox-option:active {
      background-color: #e0e0e0;
    }

    .combobox-no-results {
      color: #999;
      font-style: italic;
      cursor: default;
    }

    .combobox-no-results:hover {
      background-color: transparent;
    }
  `]
})
export class ComboboxDropdownComponent {
  @Input() options: any[] = [];
  @Input() highlightedIndex: number = -1;
  @Input() keyboardNavigationMode: boolean = false;
  @Input() listboxId: string = 'combobox-listbox';
  @Input() optionPrefix: string = 'combobox';
  @Input() ariaLabel: string = 'Suggestions';
  @Input() noResultsText: string = 'No results found';
  @Input() maxHeight: number = 200;
  @Input() optionTemplate?: TemplateRef<any>;
  @Input() valueProperty: string = 'value';
  @Input() displayProperty?: string;

  @Output() optionSelected = new EventEmitter<any>();
  @Output() highlightedIndexChange = new EventEmitter<number>();

  constructor(
    public elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  getDisplayValue(option: any): string {
    if (typeof option === 'string') {
      return option;
    }
    const displayProp = this.displayProperty || this.valueProperty;
    return option[displayProp] || '';
  }

  selectOption(option: any): void {
    this.optionSelected.emit(option);
  }

  onMouseDown(event: MouseEvent): void {
    // Prevent input from losing focus when clicking dropdown
    event.preventDefault();
  }

  onMouseEnter(index: number): void {
    this.highlightedIndex = index;
    this.highlightedIndexChange.emit(index);
    this.cdr.detectChanges();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Close dropdown if clicking outside
    if (!this.elementRef.nativeElement.contains(event.target)) {
      // This will be handled by the directive's blur event
    }
  }

  scrollToHighlighted(): void {
    if (this.highlightedIndex < 0) {
      return;
    }
    const listElement = this.elementRef.nativeElement.querySelector('.combobox-dropdown');
    const optionElement = this.elementRef.nativeElement.querySelector(`#${this.optionPrefix}-option-${this.highlightedIndex}`);

    if (listElement && optionElement) {
      const listRect = listElement.getBoundingClientRect();
      const optionRect = optionElement.getBoundingClientRect();

      if (optionRect.bottom > listRect.bottom) {
        optionElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      } else if (optionRect.top < listRect.top) {
        optionElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }
    }
  }
}
